import React, { useEffect, useState } from 'react';
import { Gift, Plus, Trash2, Save } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';

export default function AdminRewards() {
  const [settings, setSettings] = useState({
    enabled: false,
    pointsPerPound: 10
  });

  const [rewards, setRewards] = useState([]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    pointsCost: 100,
    active: true
  });

  const [saving, setSaving] = useState(false);

  // LOAD DATA
  const load = async () => {
    const [restaurant, rewardsData] = await Promise.all([
      api.getRestaurant(),
      api.getAdminRewards()
    ]);

    setSettings({
      enabled: restaurant.rewardsEnabled ?? false,
      pointsPerPound: restaurant.pointsPerPound ?? 10
    });

    setRewards(rewardsData);
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  // SAVE SETTINGS
  const saveSettings = async () => {
    setSaving(true);

    try {
      const updated = await api.updateRestaurant({
        rewardsEnabled: settings.enabled,
        pointsPerPound: Number(settings.pointsPerPound)
      });

      setSettings({
        enabled: updated.rewardsEnabled,
        pointsPerPound: updated.pointsPerPound
      });
    } finally {
      setSaving(false);
    }
  };

  // CREATE REWARD
  const createReward = async () => {
    if (!form.name.trim()) return;

    await api.createAdminReward({
      name: form.name.trim(),
      description: form.description.trim(),
      pointsCost: Number(form.pointsCost),
      active: form.active
    });

    setForm({
      name: '',
      description: '',
      pointsCost: 100,
      active: true
    });

    await load();
  };

  // TOGGLE ACTIVE
  const toggleReward = async (reward) => {
    await api.updateAdminReward(reward.id, {
      active: !reward.active
    });
    await load();
  };

  // DELETE
  const deleteReward = async (id) => {
    await api.deleteAdminReward(id);
    await load();
  };

  return (
    <div className="admin-view space-y-6">
      {/* HEADER */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Gift className="w-5 h-5 text-amber-600" />
          Rewards
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Set how customers earn points and create rewards they can redeem.
        </p>
      </div>

      {/* SETTINGS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-4">Points Settings</h3>

        <div className="admin-points-settings">
          <label className="admin-points-toggle">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            />
            <span className="admin-toggle-track"><span /></span>
            <span><strong>Enable points</strong><small>Customers earn points from qualifying orders</small></span>
          </label>

          {settings.enabled && (
          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Points per £1 spent
            </span>
            <input
              type="number"
              min="0"
              value={settings.pointsPerPound}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  pointsPerPound: Number(e.target.value)
                })
              }
              className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          )}

          <button
            onClick={saveSettings}
            disabled={saving}
            className="admin-save-settings-button bg-gray-900 text-white rounded-xl px-4 py-2 font-semibold flex items-center justify-center gap-2 hover:bg-amber-700"
          >
            <Save className="w-4 h-4" />
            Save Settings
          </button>
        </div>

        {settings.enabled && <p className="text-xs text-gray-500 mt-3">
          Example: 10 points per £1 means a £20 order earns 200 points.
        </p>}
      </div>

      {/* CREATE */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-4">Create Reward</h3>

        <div className="grid sm:grid-cols-4 gap-3">
          <input
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            placeholder="Reward name"
            className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
          />

          <input
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            placeholder="Description"
            className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
          />

          <input
            type="number"
            min="1"
            value={form.pointsCost}
            onChange={(e) =>
              setForm({
                ...form,
                pointsCost: Number(e.target.value)
              })
            }
            placeholder="Points cost"
            className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
          />

          <button
            onClick={createReward}
            className="bg-gray-900 text-white rounded-xl px-4 py-2 font-semibold flex items-center justify-center gap-2 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4" />
            Add Reward
          </button>
        </div>
      </div>

      {/* LIST */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {rewards.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            No rewards created yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {rewards.map((reward) => (
              <div key={reward.id} className="p-4 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-amber-700" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">
                    {reward.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {reward.description}
                  </p>
                  <p className="text-xs text-amber-700 font-semibold mt-1">
                    {reward.pointsCost} points
                  </p>
                </div>

                <button
                  onClick={() => toggleReward(reward)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    reward.active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {reward.active ? 'Active' : 'Inactive'}
                </button>

                <button
                  onClick={() => deleteReward(reward.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
